import React, { useCallback, useEffect, useState } from 'react';
import { Application } from '@feathersjs/feathers';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Issue, PatchIssue } from '../../app/feathers/issues/Issue';
import { useFeathers } from '../../app/util';
import { LoadingSpinner, Title } from '../../components';

interface IssueFormValues extends PatchIssue {
  key: string;
}

const emptyForm: IssueFormValues = {
  key: '',
  name: '',
  subtopics: '',
  impactIssue: true,
  candidateIssue: true,
};

const Issues = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editingIssue, setEditingIssue] = useState<Issue | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<IssueFormValues>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadIssues = useCallback(async () => {
    if (!feathers) return;

    setLoading(true);
    setLoadError('');
    try {
      const result = await feathers.getService('issues').find({
        query: {
          supportedByProfiler: true,
          $sort: { name: 1 },
          $limit: rowsPerPage,
          $skip: page * rowsPerPage,
        },
      });
      setIssues(result.data);
      setTotal(result.total);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Unable to load issues.');
    } finally {
      setLoading(false);
    }
  }, [feathers, page, rowsPerPage]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const openCreateDialog = () => {
    setEditingIssue(null);
    setForm(emptyForm);
    setSaveError('');
    setDialogOpen(true);
  };

  const openEditDialog = (issue: Issue) => {
    setEditingIssue(issue);
    setForm({
      key: issue.key,
      name: issue.name,
      subtopics: issue.subtopics || '',
      impactIssue: issue.impactIssue,
      candidateIssue: issue.candidateIssue,
    });
    setSaveError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!saving) setDialogOpen(false);
  };

  const saveIssue = async () => {
    if (!feathers) return;

    const key = form.key.trim();
    const name = form.name.trim();
    if (!name || (!editingIssue && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(key))) {
      setSaveError('Enter a name and a lowercase kebab-case key.');
      return;
    }

    const editableFields: PatchIssue = {
      name,
      subtopics: form.subtopics?.trim() || '',
      impactIssue: form.impactIssue,
      candidateIssue: form.candidateIssue,
    };

    setSaving(true);
    setSaveError('');
    try {
      if (editingIssue) {
        await feathers.getService('issues').patch(editingIssue.key, editableFields);
      } else {
        await feathers.getService('issues').create({
          key,
          ...editableFields,
          supportedByProfiler: true,
        });
      }
      setDialogOpen(false);
      if (page === 0) {
        await loadIssues();
      } else {
        setPage(0);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unable to save the issue.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <Title>Manage Issues</Title>
      <Header>
        <div>
          <Typography variant="h1">Manage Issues</Typography>
          <Button component={Link} to="/offices" style={{ marginTop: '8px', paddingLeft: 0 }}>
            Back to offices
          </Button>
        </div>
        <Button variant="contained" onClick={openCreateDialog}>
          Add Issue
        </Button>
      </Header>

      {loadError && <Alert severity="error" style={{ marginBottom: '16px' }}>{loadError}</Alert>}

      {loading ? (
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      ) : (
        <Paper>
          <TableContainer>
            <Table aria-label="Issues">
              <TableHead>
                <TableRow>
                  <TableCell>Issue name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Subtopics</TableCell>
                  <TableCell align="center">Impact issue</TableCell>
                  <TableCell align="center">Candidate issue</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.map(issue => (
                  <TableRow key={issue.key} hover>
                    <TableCell>{issue.name}</TableCell>
                    <TableCell>{issue.key}</TableCell>
                    <TableCell>{issue.subtopics || '—'}</TableCell>
                    <TableCell align="center">{issue.impactIssue ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="center">{issue.candidateIssue ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={`Edit ${issue.name}`}
                        onClick={() => openEditDialog(issue)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!issues.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No supported issues found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingIssue ? 'Edit Issue' : 'Add Issue'}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" style={{ marginBottom: '16px' }}>{saveError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            required
            label="Issue name"
            margin="normal"
            value={form.name}
            onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
          />
          <TextField
            fullWidth
            required
            disabled={Boolean(editingIssue)}
            helperText={editingIssue ? 'Issue keys cannot be changed.' : 'Lowercase kebab-case, for example: public-safety'}
            label="Key"
            margin="normal"
            value={form.key}
            onChange={event => setForm(current => ({ ...current, key: event.target.value }))}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Subtopics"
            margin="normal"
            value={form.subtopics}
            onChange={event => setForm(current => ({ ...current, subtopics: event.target.value }))}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.impactIssue}
                onChange={event => setForm(current => ({ ...current, impactIssue: event.target.checked }))}
              />
            }
            label="Impact issue"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.candidateIssue}
                onChange={event => setForm(current => ({ ...current, candidateIssue: event.target.checked }))}
              />
            }
            label="Candidate issue"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveIssue} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: calc(100% - 72px);
  min-height: calc(100% - 72px);
  padding: 36px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px;
`;

export default Issues;
